import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useParams } from "react-router-dom";
import Iframe from "react-iframe";
import {
  FaCheckCircle,
  FaFileAlt,
  FaPalette,
  FaUserGraduate,
  FaUserPlus,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const DOCUMENT_TYPE = "adm_ack";
const SELECTION_TYPE = "student";
const DEFAULT_IFRAME_HEIGHT = "300px";
const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
const SERVER_NAME = process.env.REACT_APP_SERVER_NAME;
const SYSTEM_CODE = process.env.REACT_APP_SYSTEM_CODE;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function decodeParam(value) {
  if (value == null || value === "") return "";
  try {
    return decodeURIComponent(String(value));
  } catch {
    return String(value);
  }
}

//Created By Cursor
function parseColorPair(code) {
  const parts = String(code || "").split("_");
  const color1 = parts[0] || "#cbd5e1";
  return { color1, color2: parts[1] || color1 };
}

//Created By Cursor
function Acknowledgement() {
  const loggedIn = localStorage.getItem("token") != null;
  const params = useParams();
  const studentId = decodeParam(params.student_id);
  const sessionId = decodeParam(params.Session_id || params.session_id);

  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [colorIndex, setColorIndex] = useState(0);
  const [fileUrl, setFileUrl] = useState("");
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);

  const selectedSample = samples[sampleIndex] || null;
  const colorOptions = useMemo(
    () =>
      Array.isArray(selectedSample?.color_options)
        ? selectedSample.color_options
        : [],
    [selectedSample]
  );
  const selectedColor = colorOptions[colorIndex] || null;
  const documentCaption =
    selectedSample?.document_caption ||
    selectedSample?.sample_name ||
    "Admission acknowledgement";

  //Created By Cursor
  const buildFileUrl = useCallback(
    (sample, colorOption) => {
      if (!sample?.file_name || !sample?.file_directory || !studentId) {
        return "";
      }
      const schoolCode = SessionManager.getSchoolCode();
      const colorOptionId = colorOption?.color_option_id || "";
      return (
        `${SERVER_NAME}${sample.file_directory}${sample.file_name}` +
        `?&student_ids=${JSON.stringify([studentId])}` +
        `&class_id=&section_id=` +
        `&session_id=${sessionId || ""}` +
        `&sample_id=${sample.sample_id || ""}` +
        `&selection_type=${SELECTION_TYPE}` +
        `&system_code=${SYSTEM_CODE || ""}` +
        `&color_option_id=${colorOptionId}` +
        `&school_code=${schoolCode || ""}`
      );
    },
    [studentId, sessionId]
  );

  //Created By Cursor
  const applyPreview = useCallback(
    (list, nextSampleIndex, nextColorIndex) => {
      const sample = list?.[nextSampleIndex];
      if (!sample) {
        setFileUrl("");
        return;
      }
      const colors = Array.isArray(sample.color_options)
        ? sample.color_options
        : [];
      const color = colors[nextColorIndex] || colors[0] || null;
      const url = buildFileUrl(sample, color);
      setPreviewLoading(Boolean(url));
      setFileUrl(url);
      console.log("[Acknowledgement] FILE_URL", url);
    },
    [buildFileUrl]
  );

  //Created By Cursor
  const loadSamples = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      setLoading(false);
      return;
    }
    if (!studentId) {
      myToast.notify_warning("Enrollment number is missing.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE()}getPrintableSample.php`, {
        params: {
          school_code: schoolCode,
          document_type: DOCUMENT_TYPE,
        },
      });

      if (response.data?.api_status === "OK") {
        const list = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setSamples(list);
        setSampleIndex(0);
        setColorIndex(0);
        if (list.length) {
          applyPreview(list, 0, 0);
        } else {
          myToast.notify_warning("No acknowledgement templates found.");
        }
      } else {
        setSamples([]);
        setFileUrl("");
        myToast.notify_warning(
          response.data?.api_remark || "Could not load acknowledgement."
        );
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Network error loading acknowledgement.");
    } finally {
      setLoading(false);
    }
  }, [studentId, applyPreview]);

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  //Created By Cursor — resize when embedded document posts { iframeHeight }
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.iframeHeight) {
        setIframeHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", onMessage, false);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  //Created By Cursor
  useEffect(() => {
    if (!fileUrl) return;
    setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    setPreviewLoading(true);
  }, [fileUrl]);

  //Created By Cursor
  const handleSampleSelect = useCallback(
    (index) => {
      setSampleIndex(index);
      setColorIndex(0);
      applyPreview(samples, index, 0);
    },
    [samples, applyPreview]
  );

  //Created By Cursor
  const handleColorSelect = useCallback(
    (index) => {
      setColorIndex(index);
      applyPreview(samples, sampleIndex, index);
    },
    [samples, sampleIndex, applyPreview]
  );

  if (!loggedIn) return <Redirect to="/Login" />;
  if (loading) return <MyLoader />;

  return (
    <div
      className="content payroll-page sm-page sr-page ack-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Acknowledgement</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaFileAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">{documentCaption}</h1>
              <p className="sm-hero__sub">
                document_type · {DOCUMENT_TYPE} · preview & print letter
              </p>
            </div>
          </div>
        </header>

        <div className="sr-body ack-body">
          <section className="ack-student" aria-label="Student">
            <div className="ack-student__icon" aria-hidden>
              <FaUserGraduate />
            </div>
            <div className="ack-student__meta min-w-0">
              <span className="ack-student__label">Enrollment</span>
              <strong className="ack-student__value">{studentId || "—"}</strong>
              {sessionId ? (
                <span className="ack-student__session">
                  Session · {sessionId}
                </span>
              ) : null}
            </div>
            <div className="ack-student__links">
              <Link
                to={`/AdminMain/StudentRegSuccess/${encodeURIComponent(
                  studentId
                )}/${encodeURIComponent(sessionId)}`}
                className="ack-link"
              >
                <FaCheckCircle aria-hidden /> Success
              </Link>
              <Link to="/AdminMain/StudentRegistration" className="ack-link">
                <FaUserPlus aria-hidden /> New registration
              </Link>
            </div>
          </section>

          {samples.length > 1 ? (
            <section className="ack-panel" aria-label="Templates">
              <div className="ack-panel__head">
                <FaFileAlt aria-hidden />
                <h2>Sample</h2>
              </div>
              <div className="ack-samples">
                {samples.map((item, index) => (
                  <button
                    key={item.sample_id || index}
                    type="button"
                    className={`ack-sample${
                      sampleIndex === index ? " is-active" : ""
                    }`}
                    onClick={() => handleSampleSelect(index)}
                  >
                    {item.sample_img ? (
                      <img src={item.sample_img} alt="" />
                    ) : (
                      <span className="ack-sample__fallback" aria-hidden>
                        <FaFileAlt />
                      </span>
                    )}
                    <span>{item.sample_name || `Sample ${index + 1}`}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {colorOptions.length > 0 ? (
            <section className="ack-panel" aria-label="Color options">
              <div className="ack-panel__head">
                <FaPalette aria-hidden />
                <h2>Color</h2>
              </div>
              <div className="ack-colors">
                {colorOptions.map((item, index) => {
                  const { color1, color2 } = parseColorPair(
                    item.sample_color_code
                  );
                  return (
                    <button
                      key={item.color_option_id || index}
                      type="button"
                      className={`ack-color${
                        colorIndex === index ? " is-active" : ""
                      }`}
                      onClick={() => handleColorSelect(index)}
                      title={item.sample_color_name || "Color"}
                    >
                      <span
                        className="ack-color__swatch"
                        style={{
                          background: `linear-gradient(90deg, ${color1} 50%, ${color2} 50%)`,
                        }}
                      />
                      <span>{item.sample_color_name || `Option ${index + 1}`}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          <section className="ack-preview" aria-label="Preview">
            <div className="ack-preview__head">
              <h2>Preview</h2>
              {selectedColor?.sample_color_name ? (
                <span className="ack-preview__meta">
                  {selectedSample?.sample_name || "Template"}
                  {" · "}
                  {selectedColor.sample_color_name}
                </span>
              ) : selectedSample?.sample_name ? (
                <span className="ack-preview__meta">
                  {selectedSample.sample_name}
                </span>
              ) : null}
            </div>
            <div className="ack-preview__frame">
              {previewLoading ? (
                <div className="ack-preview__loader">
                  <MyLoader />
                </div>
              ) : null}
              {fileUrl ? (
                <Iframe
                  url={fileUrl}
                  width="100%"
                  height={iframeHeight}
                  overflow="hidden"
                  scrolling="no"
                  id="iFrameAcknowledgement"
                  className="ack-iframe"
                  display="initial"
                  position="relative"
                  onLoad={() => setPreviewLoading(false)}
                />
              ) : (
                <div className="ack-preview__empty">
                  No preview available for this acknowledgement.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Acknowledgement;
